import { useCallback, useEffect, useRef, useState } from "react";
import { registerSW } from "virtual:pwa-register";

/** Periodic update check interval (15 seconds to ensure fast update detection). */
const PERIODIC_CHECK_INTERVAL_MS = 15 * 1000;

/** Restores the controls when worker activation or browser navigation does not complete. */
const UPDATE_RECOVERY_TIMEOUT_MS = 10 * 1000;

export interface UsePwaUpdateResult {
  /** Indicates whether a new version of the frontend has been installed and is waiting to activate. */
  needRefresh: boolean;
  /** Indicates whether the application shell has finished precaching for offline access. */
  offlineReady: boolean;
  /** The incoming worker is still installing its offline assets. */
  isDownloading: boolean;
  /** Installation or activation failed; manual controls must offer retry. */
  updateFailed: boolean;
  /** Indicates whether an update is in progress (activating new service worker and reloading). */
  isUpdating: boolean;
  /** Activates the waiting service worker and reloads the window to run the latest application bundle. */
  updateApp: () => Promise<void>;
  /** Dismisses the current waiting version until another worker is installed or the page reloads. */
  dismiss: () => void;
  /** Manually triggers an update check against the server. */
  checkForUpdate: (revealDismissed?: boolean) => Promise<void>;
}

/**
 * Manages the Progressive Web App (PWA) service worker lifecycle, detection of new releases,
 * and user-driven prompt updates.
 *
 * Implementation Strategy:
 * - Mounts above the identity-keyed router so registration survives authentication transitions.
 * - Listens to Workbox `onNeedRefresh`, native `registration.updatefound`, and direct `registration.waiting`.
 * - Actively polls for new frontend builds every 15 seconds, upon window focus, visibility change, and network reconnect.
 * - Listens to `navigator.serviceWorker.controllerchange` so activating the waiting worker immediately reloads to the latest build.
 * - Tracks dismissed worker identities and restores controls if a reload is canceled.
 */
export function usePwaUpdate(): UsePwaUpdateResult {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [updateFailed, setUpdateFailed] = useState(false);

  const updateSwRef = useRef<((reloadPage?: boolean) => Promise<void>) | null>(
    null,
  );
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const mountedRef = useRef(false);
  const updatingRef = useRef(false);
  const recoveryTimerRef = useRef<number | null>(null);
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);
  const dismissedWorkerRef = useRef<ServiceWorker | null>(null);

  const resetUpdating = useCallback(() => {
    if (recoveryTimerRef.current !== null) {
      window.clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
    updatingRef.current = false;
    if (mountedRef.current) setIsUpdating(false);
  }, []);

  const reloadApp = useCallback(() => {
    try {
      window.location.reload();
    } finally {
      // Canceling a beforeunload dialog does not throw; a surviving page must remain usable.
      resetUpdating();
    }
  }, [resetUpdating]);

  const notifyWaitingWorker = useCallback((worker: ServiceWorker | null) => {
    if (!mountedRef.current) return;
    if (worker) waitingWorkerRef.current = worker;
    // Native and Workbox events can report the same version repeatedly after dismissal.
    if (worker && worker === dismissedWorkerRef.current) return;
    setNeedRefresh(true);
  }, []);

  const checkForUpdate = useCallback(
    async (revealDismissed = false) => {
      if (!mountedRef.current) {
        return;
      }

      try {
        setUpdateFailed(false);
        if (revealDismissed) dismissedWorkerRef.current = null;
        const reg =
          registrationRef.current ??
          (await navigator.serviceWorker.getRegistration());
        if (!mountedRef.current) return;
        if (reg) {
          registrationRef.current = reg;
          if (reg.waiting && navigator.serviceWorker.controller) {
            notifyWaitingWorker(reg.waiting);
          }
          await reg.update();
          setIsDownloading(Boolean(reg.installing));
        } else {
          throw new Error("Service worker is not registered");
        }
      } catch (error) {
        if (mountedRef.current) setUpdateFailed(true);
        throw error;
      }
    },
    [notifyWaitingWorker],
  );

  useEffect(() => {
    // Guard against server-side rendering or environments without serviceWorker support
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    mountedRef.current = true;
    let disposed = false;
    let intervalId: number | undefined;
    let observedRegistration: ServiceWorkerRegistration | undefined;
    const observedWorkers = new Map<ServiceWorker, () => void>();

    const handleUpdateFound = () => {
      const worker = observedRegistration?.installing;
      if (disposed || !worker || observedWorkers.has(worker)) return;

      setIsDownloading(true);
      const handleStateChange = () => {
        if (disposed) return;
        if (
          worker.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          notifyWaitingWorker(worker);
        }
        if (worker.state === "installed" || worker.state === "redundant")
          setIsDownloading(false);
        if (worker.state === "redundant") setUpdateFailed(true);
        // Terminal workers no longer need observation, even while the prompt remains open.
        if (worker.state === "installed" || worker.state === "redundant") {
          worker.removeEventListener("statechange", handleStateChange);
          observedWorkers.delete(worker);
        }
      };
      observedWorkers.set(worker, handleStateChange);
      worker.addEventListener("statechange", handleStateChange);
      handleStateChange();
    };

    let hadController = Boolean(navigator.serviceWorker.controller);
    // Listen for service worker controller change to reload when the new worker takes over
    const handleControllerChange = () => {
      if (disposed) return;
      if (!hadController) {
        hadController = true;
        return;
      }
      // A different tab may activate a worker; preserve this tab's unfinished work.
      if (updatingRef.current) reloadApp();
      else notifyWaitingWorker(null);
    };
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      handleControllerChange,
    );

    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        if (!disposed) {
          notifyWaitingWorker(
            registrationRef.current?.waiting ?? waitingWorkerRef.current,
          );
        }
      },
      onNeedReload: handleControllerChange,
      onOfflineReady() {
        if (!disposed) setOfflineReady(true);
      },
      onRegisteredSW(_swUrl, registration) {
        // Registration may finish after cleanup; never attach resources to a retired effect.
        if (disposed || !registration) return;
        registrationRef.current = registration;
        observedRegistration = registration;

        if (registration.waiting && navigator.serviceWorker.controller) {
          notifyWaitingWorker(registration.waiting);
        }

        registration.addEventListener("updatefound", handleUpdateFound);
        handleUpdateFound();

        // Check immediately and then retain the existing polling cadence.
        registration.update().catch(() => {});

        intervalId = window.setInterval(() => {
          if (navigator.onLine) {
            registration.update().catch(() => {});
          }
        }, PERIODIC_CHECK_INTERVAL_MS);
      },
      onRegisterError(error) {
        if (!disposed) {
          setUpdateFailed(true);
          console.error("PWA Service Worker registration failed:", error);
        }
      },
    });

    updateSwRef.current = updateSW;

    // Trigger update checks on user interactions: focus, visibility change, online
    const handleFocus = () => {
      void checkForUpdate().catch(() => {});
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkForUpdate().catch(() => {});
      }
    };
    const handleOnline = () => {
      void checkForUpdate().catch(() => {});
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    // Expose helpers on window for manual DevTools testing
    const win = window as unknown as {
      __kayerCheckForUpdate?: () => Promise<void>;
      __kayerSimulateUpdate?: () => void;
    };
    win.__kayerCheckForUpdate = () => checkForUpdate(true);
    win.__kayerSimulateUpdate = () => setNeedRefresh(true);

    return () => {
      disposed = true;
      mountedRef.current = false;
      resetUpdating();
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
      observedRegistration?.removeEventListener(
        "updatefound",
        handleUpdateFound,
      );
      for (const [worker, listener] of observedWorkers) {
        worker.removeEventListener("statechange", listener);
      }
      observedWorkers.clear();
      registrationRef.current = null;
      updateSwRef.current = null;
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        handleControllerChange,
      );
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      delete win.__kayerCheckForUpdate;
      delete win.__kayerSimulateUpdate;
    };
  }, [checkForUpdate, notifyWaitingWorker, reloadApp, resetUpdating]);

  const updateApp = useCallback(async () => {
    if (!mountedRef.current || updatingRef.current) return;
    updatingRef.current = true;
    setIsUpdating(true);
    setUpdateFailed(false);
    // The update callback sends a message rather than awaiting activation, which can stall.
    recoveryTimerRef.current = window.setTimeout(
      resetUpdating,
      UPDATE_RECOVERY_TIMEOUT_MS,
    );
    try {
      if (updateSwRef.current) {
        await updateSwRef.current(true);
      }
      if (!mountedRef.current) return;

      // Direct native fallback: post SKIP_WAITING to waiting worker
      const reg =
        registrationRef.current ??
        (await navigator.serviceWorker.getRegistration());
      if (!mountedRef.current) return;
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: "SKIP_WAITING" });
      } else {
        reloadApp();
      }
    } catch (error) {
      console.error("Failed to apply service worker update:", error);
      setUpdateFailed(true);
      resetUpdating();
    }
  }, [reloadApp, resetUpdating]);

  const dismiss = useCallback(() => {
    dismissedWorkerRef.current =
      registrationRef.current?.waiting ?? waitingWorkerRef.current;
    setNeedRefresh(false);
  }, []);

  return {
    needRefresh,
    offlineReady,
    isDownloading,
    updateFailed,
    isUpdating,
    updateApp,
    dismiss,
    checkForUpdate,
  };
}
